import React, { useCallback, useEffect, useState } from "react";

import { makeStyles } from "tss-react/mui";

import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";

import CloseIcon from "@mui/icons-material/Close";

import DqmClass from "../../../service/dqm";
import { KMKDrawer, ButtonUI } from "../../Common";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/actions";
import MultipleChipInput from "../../Common/MultipleChipInput";
import { regexBasicEmail } from "../../../util/regularExpress";
import Switch from "../../Common/Switch";

const drawerWidth = 420;

const useStyles = makeStyles()((theme) => ({
  heading: {
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
    "& span": {
      fontWeight: "normal",
    },
  },
  heading2: {
    fontWeight: "bold",
    fontSize: "1rem",
    "& span": {
      fontWeight: "normal",
    },
  },
  closeIcon: {
    padding: 0,
  },
  container: {
    height: "100%",
    padding: "25px 30px",
    "& .errorMessage .MuiPaper-root": {
      marginTop: "20px",
      alignItems: "flex-start",
      border: "none",
    },
    "& .errorMessage .MuiAlert-message": {
      color: "#C72C2C",
    },
    "& .errorMessage": {
      marginBottom: 0,
    },
  },
  alertMessage: {
    "& .MuiPaper-root": {
      marginTop: "9px",
      alignItems: "center",
      borderRadius: 10,
    },
    "& .MuiFormControl-root": { color: "#a2a2a2" },
    "& .MuiAlert-icon": {
      padding: 0,
      backgroundColor: "#67A7D5",
      borderTopLeftRadius: "10px",
      borderBottomLeftRadius: "9px",
      height: "71px",
      alignItems: "center",
      "& .MuiSvgIcon-root": {
        height: "100%",
        color: "#FFFFFF",
      },
    },
    "& .MuiAlert-message": {
      padding: "15px 5px",
      color: "#A2A2A2",
    },
  },
}));

const AlertRecipientsPanel = ({ Workspace_id, toggleAlertRecipients }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  // For Managing states
  const [emailEnable, setEmailEnable] = useState(false);
  const [emails, setEmails] = useState([]);
  const [inputValues, setInputValues] = useState("");

  const [criticalAlert, setCriticalAlert] = useState(true);
  const [acceptableAlert, setAcceptableAlert] = useState(false);

  const handleClear = useCallback(() => {
    setEmails([]);
    setInputValues("");
  }, []);

  const handleEmailChange = useCallback((newValue) => {
    const lastEmail = newValue[newValue.length - 1];
    if (regexBasicEmail(lastEmail)) setEmails(newValue);
    else setInputValues(lastEmail || "");
  }, []);

  const onDelete = useCallback((option) => setEmails((prev) => prev.filter((email) => email !== option)), []);

  const processEmails = useCallback(
    (emailString) => {
      const emailArray = emailString.split(",").map((email) => email.trim());

      let newEmails = [...emails];
      emailArray.forEach((email) => {
        if (regexBasicEmail(email) && !newEmails.includes(email)) newEmails.push(email.toLowerCase());
      });

      setEmails(newEmails);
      setInputValues("");
    },
    [emails]
  );

  const handleKeyPress = useCallback(
    (event) => {
      const trimmedValue = inputValues?.trim();
      if (event.key === "Enter" && emails?.some((email) => email?.toLowerCase() === trimmedValue?.toLowerCase())) {
        event.preventDefault();
        setInputValues("");
      }
      if (event.key === ",") {
        event.preventDefault();
        if (trimmedValue) processEmails(trimmedValue);
      }
    },
    [emails, inputValues, processEmails]
  );

  const handlePaste = useCallback(
    (event) => {
      const pastedData = event.clipboardData.getData("text");
      event.preventDefault();
      if (pastedData) processEmails(pastedData);
    },
    [processEmails]
  );

  useEffect(() => {
    setLoading(true);

    const dqmClass = new DqmClass();
    dqmClass.getAlertRecipients(Workspace_id).then((response) => {
      const { data, success } = response;
      if (success) {
        setEmailEnable(data.Enable === 1);
        setEmails(data.Recipients ?? []);
        setCriticalAlert(!!data.CriticalAlert);
        setAcceptableAlert(!!data.AcceptableAlert);
      }

      setLoading(false);
    });
  }, [Workspace_id]);

  const handleSave = async () => {
    setLoading(true);

    const dqmClass = new DqmClass();
    const response = await dqmClass.updateAlertRecipients(Workspace_id, {
      AlertType: 1,
      emailEnable,
      emails,
      criticalAlert: criticalAlert ? 1 : 0,
      acceptableAlert: acceptableAlert ? 1 : 0,
    });

    if (response.success) {
      dispatch(showToast({ message: response.message, type: "success" }));
      toggleAlertRecipients();
    }

    setLoading(false);
  };

  return (
    <KMKDrawer
      anchor="right"
      open={true}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "450px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Toolbar />
      <Box className={classes.container} role="presentation" sx={{ maxHeight: "100%", overflowY: "auto" }}>
        <Grid container spacing={2}>
          <Grid size={10}>
            <Typography className={classes.heading} variant="h6" gutterBottom component="div">
              Configure Recipients
            </Typography>
          </Grid>
          <Grid size={2}>
            <Button className={classes.closeIcon} onClick={toggleAlertRecipients}>
              <CloseIcon />
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={0}>
          <Grid size={12}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <FormControlLabel
                control={<Checkbox checked={emailEnable} />}
                label="Enable Alert Email"
                onChange={() => setEmailEnable((prev) => !prev)}
              />
              <ButtonUI
                variant="text"
                color="secondary"
                style={{ margin: "7px  10px 7px 0", fontSize: "14px" }}
                onMouseDown={(e) => handleClear()}
                disabled={!emails?.length}
              >
                Clear
              </ButtonUI>
            </Box>
            <MultipleChipInput
              placeholder="Enter Email Recipients"
              value={emails ?? []}
              inputValue={inputValues ?? ""}
              helperText="Press Enter or use commas to add email addresses separately."
              handleonInputChange={(event, newInputValue) => setInputValues(newInputValue)}
              handleOnChange={(event, newValue) => handleEmailChange(newValue)}
              onDelete={(option) => onDelete(option)}
              handleKeyPress={(e) => handleKeyPress(e)}
              handlePaste={(e) => handlePaste(e)}
              disableClearable={true}
              maxHeight="380px"
            />
          </Grid>
          <Grid sx={{ marginTop: 1 }} size={12}>
            <FormGroup aria-label="position" row>
              <FormControlLabel
                value={criticalAlert}
                control={<Switch className={classes.Switch} />}
                label="Trigger Alert on any Critical findings"
                labelPlacement="end"
                sx={{ margin: "0" }}
                onChange={(e) => setCriticalAlert(e.target.checked)}
                checked={criticalAlert}
                disabled={loading}
              />
            </FormGroup>
          </Grid>
          <Grid sx={{ marginBottom: 1 }} size={12}>
            <FormGroup aria-label="position" row>
              <FormControlLabel
                value={acceptableAlert}
                control={<Switch className={classes.Switch} />}
                label="Trigger Alert on any Warning findings"
                labelPlacement="end"
                sx={{ margin: "0" }}
                onChange={(e) => setAcceptableAlert(e.target.checked)}
                checked={acceptableAlert}
                disabled={loading}
              />
            </FormGroup>
          </Grid>
        </Grid>

        <Box my={2}>
          <ButtonUI disabled={loading} variant="contained" color="success" onClick={handleSave}>
            Save
          </ButtonUI>
          <ButtonUI
            variant="contained"
            color="secondary"
            style={{ marginLeft: 15 }}
            onClick={toggleAlertRecipients}
            disabled={loading}
          >
            Cancel
          </ButtonUI>
        </Box>
      </Box>
    </KMKDrawer>
  );
};

export default AlertRecipientsPanel;
